import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SuccessDialog from '../../components/SuccessDialog';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { getApiUrl } from '../../utils/apiUrl';
import './admin-css/UploadData-light.css';
import './admin-css/UploadData-dark.css';

interface Company {
  _id: string;
  companyName: string;
  companyEmail: string;
  userCount: number;
  adminCount: number;
  users: Array<{
    _id: string;
    clientName: string;
    username: string;
    role: string;
  }>;
}

interface UploadResult {
  success: boolean;
  message: string;
  upload?: {
    id: string;
    originalFileName: string;
    recordCount: number;
    version: number;
    columns: string[];
  };
  error?: string;
}

interface BuyingIntentUploadResult {
  success: boolean;
  message: string;
  data?: {
    recordCount: number;
    genericCount: number;
    brandedCount: number;
    clientCount?: number;
    competitorCount?: number;
    unclassifiedCount?: number;
  };
  error?: string;
}

const UploadData: React.FC = () => {
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  
  // Set theme class on body for CSS targeting
  useEffect(() => {
    // Remove existing theme classes
    document.body.classList.remove('upload-data-dark-mode', 'upload-data-light-mode');
    
    // Add the correct theme class
    if (isDarkMode) {
      document.body.classList.add('upload-data-dark-mode');
    } else {
      document.body.classList.add('upload-data-light-mode');
    }
  }, [isDarkMode]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [loadingClients, setLoadingClients] = useState(true);
  
  // Buying Intent Uploader State
  const [buyingIntentFile, setBuyingIntentFile] = useState<File | null>(null);
  const [buyingIntentUploading, setBuyingIntentUploading] = useState(false);
  const [buyingIntentProgress, setBuyingIntentProgress] = useState(0);
  const [buyingIntentResult, setBuyingIntentResult] = useState<BuyingIntentUploadResult | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 5000,
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/companies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const companiesData: Company[] = response.data.data.map((company: any) => {
          const users = company.users || [];
          const adminCount = users.filter((user: any) => user.role === 'admin').length;
          
          return {
            _id: company._id,
            companyName: company.companyName,
            companyEmail: company.companyEmail,
            userCount: users.length,
            adminCount: adminCount,
            users: users.map((user: any) => ({
              _id: user._id,
              clientName: user.clientName,
              username: user.username,
              role: user.role
            }))
          };
        });
        
        setCompanies(companiesData);
      }
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Loading Companies',
        message: error.response?.data?.message || 'Failed to fetch companies',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['.csv'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        setSuccessDialogConfig({
          title: 'Invalid File Type',
          message: 'Invalid file type. Only CSV files are allowed.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setSuccessDialogConfig({
          title: 'File Too Large',
          message: 'File size must be less than 10MB.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }

      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleBuyingIntentFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['.csv'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        setSuccessDialogConfig({
          title: 'Invalid File Type',
          message: 'Invalid file type. Only CSV files are allowed.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setSuccessDialogConfig({
          title: 'File Too Large',
          message: 'File size must be less than 10MB.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }

      setBuyingIntentFile(file);
      setBuyingIntentResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCompanyId) {
      setSuccessDialogConfig({
        title: 'Missing Selection',
        message: 'Please select a company and file to upload.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const selectedCompany = companies.find((company: Company) => company._id === selectedCompanyId);
      if (!selectedCompany) {
        setSuccessDialogConfig({
          title: 'Company Not Found',
          message: 'Selected company not found',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }
      
      formData.append('companyId', selectedCompany._id);

      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/api/upload/admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total 
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        }
      );

      setUploadResult(response.data);
      
      if (response.data.success) {
        const selectedCompany = companies.find((company: Company) => company._id === selectedCompanyId);
        setSuccessDialogConfig({
          title: 'File Uploaded Successfully',
          message: `The file has been uploaded and processed successfully for ${selectedCompany?.companyName || 'the selected company'}. ${response.data.upload?.recordCount || 0} records were processed.`,
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
        setSelectedFile(null);
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload file';
      setUploadResult({
        success: false,
        message: errorMessage,
        error: error.response?.data?.error
      });
      setSuccessDialogConfig({
        title: 'Upload Failed',
        message: errorMessage,
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBuyingIntentUpload = async () => {
    if (!buyingIntentFile || !selectedCompanyId) {
      setSuccessDialogConfig({
        title: 'Missing Selection',
        message: 'Please select a company and buying intent file to upload.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      return;
    }

    setBuyingIntentUploading(true);
    setBuyingIntentProgress(0);
    setBuyingIntentResult(null);

    try {
      const formData = new FormData();
      formData.append('file', buyingIntentFile);
      
      const selectedCompany = companies.find((company: Company) => company._id === selectedCompanyId);
      if (!selectedCompany) {
        setSuccessDialogConfig({
          title: 'Company Not Found',
          message: 'Selected company not found',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        return;
      }
      
      formData.append('companyId', selectedCompany._id);

      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/api/upload/admin/buying-intent`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total 
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setBuyingIntentProgress(progress);
          },
        }
      );

      setBuyingIntentResult(response.data);
      
      if (response.data.success) {
        const selectedCompany = companies.find((company: Company) => company._id === selectedCompanyId);
        setSuccessDialogConfig({
          title: 'Buying Intent Data Uploaded Successfully',
          message: `The buying intent data has been uploaded and processed successfully for ${selectedCompany?.companyName || 'the selected company'}. ${response.data.data?.recordCount || 0} records were processed (${response.data.data?.genericCount || 0} generic, ${response.data.data?.brandedCount || 0} branded).`,
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
        setBuyingIntentFile(null);
        const fileInput = document.getElementById('buying-intent-file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload buying intent file';
      setBuyingIntentResult({
        success: false,
        message: errorMessage,
        error: error.response?.data?.error
      });
      setSuccessDialogConfig({
        title: 'Upload Failed',
        message: errorMessage,
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setBuyingIntentUploading(false);
      setBuyingIntentProgress(0);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      {/* Page Header - Completely outside the container */}
      <Box className="upload-page-header">
        <Typography 
          variant="h2" 
          className={isDarkMode ? "upload-page-title-dark" : "upload-page-title-light"}
        >
          Upload Client Data
        </Typography>
      </Box>

      <Box 
        className={`upload-data-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
      >
        {/* Main Container */}
        <Box>
        {/* Unified Container */}
        <Card 
          className="upload-main-card"
        >
           {/* Company Selection */}
           <Box sx={{ mb: 4 }}>
             <Typography 
               variant="h6" 
               className="upload-company-title"
               sx={{ 
                 fontWeight: 'bold',
                 color: '#606060',
                 fontFamily: '"Nunito Sans", sans-serif',
                 mb: 1,
                 pl: 2,
                 fontSize: '1.1rem',
               }}
             >
               Select Company
             </Typography>
             <Typography 
               variant="body2" 
               className="upload-description"
               sx={{ 
                 color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)',
                 mb: 3,
                 fontFamily: '"Nunito Sans", sans-serif',
                 pl: 2,
               }}
             >
               Select the company to upload data for. All its users will have access.
             </Typography>
            
             <FormControl 
               fullWidth 
               className="upload-company-select"
               disabled={loadingClients || uploading || buyingIntentUploading}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   borderRadius: '30px',
                   backgroundColor: isDarkMode ? '#191919' : 'rgba(213, 213, 213, 0.25)',
                   border: isDarkMode ? 'none' : '1px solid',
                   borderColor: isDarkMode ? 'transparent' : 'rgba(0, 0, 0, 0.23)',
                   '& fieldset': {
                     border: 'none',
                   },
                   '&:hover': {
                     borderColor: 'rgba(0, 0, 0, 0.5)',
                   },
                   '&.Mui-focused': {
                     borderColor: '#1976d2',
                   },
                 },
                 '& .MuiInputLabel-root': {
                   color: 'rgba(0, 0, 0, 0.6)',
                   '&.Mui-focused': {
                     color: '#1976d2',
                   },
                 },
               }}
             >
               <Select
                 value={selectedCompanyId}
                 onChange={(e) => setSelectedCompanyId(e.target.value)}
                 displayEmpty
                 renderValue={(selected) => {
                   if (!selected) {
                     return <span style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(32, 34, 36, 0.6)' }}>Select Company</span>;
                   }
                   const selectedCompany = companies.find(company => company._id === selected);
                   return selectedCompany ? selectedCompany.companyName : selected;
                 }}
                 IconComponent={ArrowDownIcon}
                 sx={{
                   color: isDarkMode ? '#ffffff' : '#202224',
                   '& .MuiSelect-icon': {
                     color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                   },
                 }}
                 MenuProps={{
                   PaperProps: {
                     sx: {
                       borderRadius: '12px',
                       border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.23)',
                       backgroundColor: isDarkMode ? '#191919' : theme.palette.background.paper,
                       boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.04)',
                       mt: 1,
                       '& .MuiMenuItem-root': {
                         borderRadius: '30px',
                         margin: '4px 8px',
                         color: isDarkMode ? '#ffffff' : '#202224',
                         '&:hover': {
                           backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                         },
                         '&.Mui-selected': {
                           backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                           '&:hover': {
                             backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.15)',
                           },
                         },
                       },
                     },
                   },
                 }}
               >
                {companies.map((company: Company) => (
                  <MenuItem key={company._id} value={company._id}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: isDarkMode ? '#ffffff' : '#202224' }}>
                      {company.companyName}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Two Column Layout */}
          <Box className="upload-two-column" sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
            
             {/* LEFT COLUMN - Lead Data Upload */}
             <Box sx={{ flex: 1 }}>
               <Typography 
                 variant="h5" 
                 className="upload-lead-title"
                 sx={{ 
                   fontWeight: 'bold',
                   color: isDarkMode ? '#ffffff' : '#606060',
                   fontFamily: '"Nunito Sans", sans-serif',
                   mb: 2,
                   pl: 2,
                 }}
               >
                 Lead Data Upload
               </Typography>
               <Typography 
                 variant="body1" 
                 className="upload-lead-description"
                 sx={{ 
                   color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)',
                   mb: 3,
                   fontFamily: '"Nunito Sans", sans-serif',
                   pl: 2,
                 }}
               >
                 Upload CSVs with company info, buying intent, and contact details.
               </Typography>

               {/* Lead File Upload */}
               <Box sx={{ mb: 4 }}>
                 <Box
                   className="upload-file-area"
                   sx={{
                     p: 2,
                     borderRadius: '30px',
                     backgroundColor: isDarkMode ? '#191919' : 'rgba(213, 213, 213, 0.25)',
                     border: isDarkMode ? 'none' : '1px solid',
                     borderColor: isDarkMode ? 'transparent' : 'rgba(0, 0, 0, 0.23)',
                     cursor: uploading ? 'not-allowed' : 'pointer',
                     transition: 'all 0.3s ease',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                     '&:hover': {
                       borderColor: uploading ? (isDarkMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.23)') : (isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                     },
                   }}
                   onClick={() => {
                     if (!uploading) {
                       document.getElementById('file-upload')?.click();
                     }
                   }}
                 >
                   <input
                     id="file-upload"
                     type="file"
                     accept=".csv"
                     style={{ display: 'none' }}
                     onChange={handleFileSelect}
                     disabled={uploading}
                   />
                   
                   <Box sx={{ flex: 1 }}>
                     {selectedFile ? (
                       <Typography variant="body1" className="upload-file-text" sx={{ color: isDarkMode ? '#ffffff' : '#202224', fontFamily: '"Nunito Sans", sans-serif' }}>
                         {selectedFile.name}
                       </Typography>
                     ) : (
                       <Typography variant="body1" className="upload-file-placeholder" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(32, 34, 36, 0.6)', fontFamily: '"Nunito Sans", sans-serif' }}>
                         Click to select lead data file
                       </Typography>
                     )}
                   </Box>
                   
                   <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M9.625 16.5V5.29375L6.05 8.86875L4.125 6.875L11 0L17.875 6.875L15.95 8.86875L12.375 5.29375V16.5H9.625ZM2.75 22C1.99375 22 1.34658 21.731 0.8085 21.1929C0.270416 20.6548 0.000916667 20.0072 0 19.25V15.125H2.75V19.25H19.25V15.125H22V19.25C22 20.0062 21.731 20.6539 21.1929 21.1929C20.6548 21.7319 20.0072 22.0009 19.25 22H2.75Z" fill={isDarkMode ? '#F5F6FA' : '#202224'}/>
                   </svg>
                 </Box>
                 <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)', fontFamily: '"Nunito Sans", sans-serif', fontSize: '0.7rem', mt: 1, display: 'block', pl: 2 }}>
                   Maximum file size: 10MB
                 </Typography>
               </Box>

              {/* Upload Progress */}
              {uploading && (
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)',
                      fontFamily: '"Nunito Sans", sans-serif',
                      mb: 1,
                    }}
                  >
                    Uploading... {uploadProgress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress}
                    sx={{
                      borderRadius: '30px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                        borderRadius: '30px',
                      },
                    }}
                  />
                </Box>
              )}

              {/* Upload Button */}
              <Button
                variant="contained"
                size="large"
                className="upload-button-primary"
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                onClick={handleUpload}
                disabled={!selectedFile || !selectedCompanyId || uploading}
                fullWidth
                sx={{ 
                  mb: 3,
                  borderRadius: '30px',
                  backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                  color: isDarkMode ? '#202224' : '#ffffff',
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#191919' : 'rgba(213, 213, 213, 0.25)',
                    color: '#202224',
                    cursor: (!selectedFile || !selectedCompanyId || uploading) ? 'not-allowed' : 'pointer',
                  },
                  '&:disabled': {
                    backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                    color: isDarkMode ? '#202224' : '#ffffff',
                    cursor: 'not-allowed',
                    opacity: 1,
                  },
                }}
              >
                {uploading ? 'Processing...' : 'Upload Lead Data'}
              </Button>

            {/* Upload Result */}
            {uploadResult && (
              <Alert 
                severity={uploadResult.success ? 'success' : 'error'}
                icon={uploadResult.success ? <SuccessIcon /> : <ErrorIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body1" gutterBottom>
                  {uploadResult.message}
                </Typography>
                
                {uploadResult.success && uploadResult.upload && (
                  <List dense>
                    <ListItem>
                      <ListItemIcon><InfoIcon /></ListItemIcon>
                      <ListItemText
                        primary="File Details"
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Chip label={`${uploadResult.upload.recordCount} records`} size="small" sx={{ mr: 1 }} />
                            <Chip label={`Version ${uploadResult.upload.version}`} size="small" sx={{ mr: 1 }} />
                            <Chip label={`${uploadResult.upload.columns.length} columns`} size="small" />
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                )}
                
                {uploadResult.error && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    Error: {uploadResult.error}
                  </Typography>
                )}
              </Alert>
            )}

            </Box>

             {/* RIGHT COLUMN - Buying Intent Upload */}
             <Box sx={{ flex: 1 }}>
               <Typography 
                 variant="h5" 
                 className="upload-intent-title"
                 sx={{ 
                   fontWeight: 'bold',
                   color: isDarkMode ? '#ffffff' : '#606060',
                   fontFamily: '"Nunito Sans", sans-serif',
                   mb: 2,
                   pl: 2,
                 }}
               >
                 Buying Intent Data Upload
               </Typography>
               <Typography 
                 variant="body1" 
                 className="upload-intent-description"
                 sx={{ 
                   color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)',
                   mb: 3,
                   fontFamily: '"Nunito Sans", sans-serif',
                   pl: 2,
                 }}
               >
                 Upload CSVs with buying intent keywords and categories.
               </Typography>

               {/* Buying Intent File Upload */}
               <Box sx={{ mb: 4 }}>
                 <Box
                   sx={{
                     p: 2,
                     borderRadius: '30px',
                     backgroundColor: isDarkMode ? '#191919' : 'rgba(213, 213, 213, 0.25)',
                     border: isDarkMode ? 'none' : '1px solid',
                     borderColor: isDarkMode ? 'transparent' : 'rgba(0, 0, 0, 0.23)',
                     cursor: buyingIntentUploading ? 'not-allowed' : 'pointer',
                     transition: 'all 0.3s ease',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                     '&:hover': {
                       borderColor: buyingIntentUploading ? (isDarkMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.23)') : (isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                     },
                   }}
                   onClick={() => {
                     if (!buyingIntentUploading) {
                       document.getElementById('buying-intent-file-upload')?.click();
                     }
                   }}
                 >
                   <input
                     id="buying-intent-file-upload"
                     type="file"
                     accept=".csv"
                     style={{ display: 'none' }}
                     onChange={handleBuyingIntentFileSelect}
                     disabled={buyingIntentUploading}
                   />
                   
                   <Box sx={{ flex: 1 }}>
                     {buyingIntentFile ? (
                       <Typography variant="body1" sx={{ color: isDarkMode ? '#ffffff' : '#202224', fontFamily: '"Nunito Sans", sans-serif' }}>
                         {buyingIntentFile.name}
                       </Typography>
                     ) : (
                       <Typography variant="body1" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(32, 34, 36, 0.6)', fontFamily: '"Nunito Sans", sans-serif' }}>
                         Click to select buying intent file
                       </Typography>
                     )}
                   </Box>
                   
                   <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M9.625 16.5V5.29375L6.05 8.86875L4.125 6.875L11 0L17.875 6.875L15.95 8.86875L12.375 5.29375V16.5H9.625ZM2.75 22C1.99375 22 1.34658 21.731 0.8085 21.1929C0.270416 20.6548 0.000916667 20.0072 0 19.25V15.125H2.75V19.25H19.25V15.125H22V19.25C22 20.0062 21.731 20.6539 21.1929 21.1929C20.6548 21.7319 20.0072 22.0009 19.25 22H2.75Z" fill={isDarkMode ? '#F5F6FA' : '#202224'}/>
                   </svg>
                 </Box>
                 <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)', fontFamily: '"Nunito Sans", sans-serif', fontSize: '0.7rem', mt: 1, display: 'block', pl: 2 }}>
                   Maximum file size: 10MB
                 </Typography>
               </Box>

              {/* Buying Intent Upload Progress */}
              {buyingIntentUploading && (
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.6)',
                      fontFamily: '"Nunito Sans", sans-serif',
                      mb: 1,
                    }}
                  >
                    Uploading buying intent data... {buyingIntentProgress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={buyingIntentProgress}
                    sx={{
                      borderRadius: '30px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                        borderRadius: '30px',
                      },
                    }}
                  />
                </Box>
              )}

              {/* Buying Intent Upload Button */}
              <Button
                variant="contained"
                size="large"
                className="upload-button-primary"
                startIcon={buyingIntentUploading ? <CircularProgress size={20} color="inherit" /> : null}
                onClick={handleBuyingIntentUpload}
                disabled={!buyingIntentFile || !selectedCompanyId || buyingIntentUploading}
                fullWidth
                sx={{ 
                  mb: 3,
                  borderRadius: '30px',
                  backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                  color: isDarkMode ? '#202224' : '#ffffff',
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#191919' : 'rgba(213, 213, 213, 0.25)',
                    color: '#202224',
                    cursor: (!buyingIntentFile || !selectedCompanyId || buyingIntentUploading) ? 'not-allowed' : 'pointer',
                  },
                  '&:disabled': {
                    backgroundColor: isDarkMode ? '#ffffff' : '#1B4672',
                    color: isDarkMode ? '#202224' : '#ffffff',
                    cursor: 'not-allowed',
                    opacity: 1,
                  },
                }}
              >
                {buyingIntentUploading ? 'Processing Buying Intent Data...' : 'Upload Intent Data'}
              </Button>

            {/* Buying Intent Upload Result */}
            {buyingIntentResult && (
              <Alert 
                severity={buyingIntentResult.success ? 'success' : 'error'}
                icon={buyingIntentResult.success ? <SuccessIcon /> : <ErrorIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body1" gutterBottom>
                  {buyingIntentResult.message}
                </Typography>
                
                {buyingIntentResult.success && buyingIntentResult.data && (
                  <List dense>
                    <ListItem>
                      <ListItemIcon><InfoIcon /></ListItemIcon>
                      <ListItemText
                        primary="Buying Intent Data Details"
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Chip label={`${buyingIntentResult.data.recordCount} total records`} size="small" sx={{ mr: 1 }} />
                            <Chip label={`${buyingIntentResult.data.genericCount} generic`} size="small" sx={{ mr: 1 }} />
                            <Chip label={`${buyingIntentResult.data.brandedCount} branded`} size="small" />
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                )}
                
                {buyingIntentResult.error && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    Error: {buyingIntentResult.error}
                  </Typography>
                )}
              </Alert>
            )}

            </Box>
          </Box>
        </Card>

        {/* Success Dialog */}
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          title={successDialogConfig.title}
          message={successDialogConfig.message}
          buttonText="Got it"
          autoCloseDelay={5000}
        />
      </Box>
    </Box>
    </>
  );
};

export default UploadData; 